/**
 * Announcement Bar — Rotating Text Controller
 * Handles auto-rotation, pause on hover, and dot navigation
 */
class AnnouncementBarRotator extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
    this.slides = [];
    this.dots = [];
    this.isHovered = false;
    this.isPaused = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.announcement-bar-slide'));
    this.dots = Array.from(this.querySelectorAll('.announcement-bar-dot'));
    this.progressBar = this.querySelector('.announcement-bar-progress');

    if (this.slides.length <= 1) return;

    this.autoRotate = this.dataset.autoRotate === 'true';
    this.speed = parseInt(this.dataset.speed, 10) || 5;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    this.bindEvents();

    if (this.autoRotate) {
      this.startTimer();
    }
  }

  disconnectedCallback() {
    this.stopTimer();
  }

  bindEvents() {
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.resetTimer();
      });
    });

    // Pause on hover
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.stopTimer();
        if (this.progressBar) {
          this.progressBar.style.animationPlayState = 'paused';
        }
      });

      this.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (this.autoRotate) {
          this.startTimer();
        }
      });
    }

    // Reduced motion preference
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionQuery.matches) {
      this.autoRotate = false;
      this.stopTimer();
    }
    motionQuery.addEventListener('change', (e) => {
      if (e.matches) {
        this.autoRotate = false;
        this.stopTimer();
      }
    });
  }

  goToSlide(index) {
    if (index === this.currentIndex) return;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    // Animate out
    currentSlide.classList.remove('is-active');
    currentSlide.classList.add('is-exiting');

    // Animate in
    nextSlide.classList.add('is-active');

    // Clean up exiting class after transition
    setTimeout(() => {
      currentSlide.classList.remove('is-exiting');
    }, 500);

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    this.currentIndex = index;
    this.startProgressBar();
  }

  nextSlide() {
    const next = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(next);
  }

  startTimer() {
    this.stopTimer();
    this.startProgressBar();
    this.timer = setInterval(() => {
      this.nextSlide();
    }, this.speed * 1000);
  }

  stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  resetTimer() {
    if (this.autoRotate && !this.isHovered) {
      this.startTimer();
    }
  }

  startProgressBar() {
    if (!this.progressBar) return;
    // Reset
    this.progressBar.classList.remove('is-animating');
    this.progressBar.style.width = '0%';
    this.progressBar.style.transitionDuration = '0s';

    // Force reflow
    void this.progressBar.offsetWidth;

    // Animate
    this.progressBar.style.transitionDuration = this.speed + 's';
    this.progressBar.classList.add('is-animating');
  }
}

if (!customElements.get('announcement-bar-rotator')) {
  customElements.define('announcement-bar-rotator', AnnouncementBarRotator);
}
